import { useSession } from "@/services/auth0/session";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;

// ========================= Carrier Enums =========================

// No CarrierType: there is no such concept server-side. How EZX procures from
// a carrier is a SupplyChannel, and a carrier can have several at once.

export enum ResolutionMode {
  SHOW_BOTH = "SHOW_BOTH",
  PREFER_PROVIDER = "PREFER_PROVIDER",
  PREFER_LOWEST = "PREFER_LOWEST",
}

// ========================= Carrier Types =========================

export interface CarrierHealthStatus {
  status: "healthy" | "degraded" | "offline" | "unconfigured";
  responseTime?: number;
  successRate?: number;
  lastCheck?: string;
  message?: string;
}

export interface CarrierCredentialStatus {
  configured: boolean;
  required: boolean;
  provider?: string;
  fields?: {
    name: string;
    configured: boolean;
  }[];
}

export type CarrierVisibility = "PUBLIC" | "RESTRICTED";

export interface CarrierPurchasingOption {
  id: string;
  apiProviderId: string | null;
  credentialId: string | null;
  active: boolean;
  priority: number;
  apiProvider: {
    id: string;
    code: string;
    name: string;
    active?: boolean;
  } | null;
}

/** EZX configures coverage for account-only integrations. */
export function isExternalOnlyCarrier(
  carrier: Pick<Carrier, "channels">
): boolean {
  const channels = (carrier.channels ?? []).filter((channel) => channel.active);
  return (
    channels.some((channel) => channel.apiProviderId !== null) &&
    !channels.some((channel) => channel.apiProviderId === null)
  );
}

export interface Carrier {
  /** The carrier list currently returns active channels only. */
  channels?: CarrierPurchasingOption[];
  id: string;
  code: string; // "ups", "fedex", "local"
  name: string; // "UPS", "FedEx", "Local Fleet"
  // No `type`: how EZX procures from a carrier is a SupplyChannel, not a
  // property of the carrier. The server has never sent this field.
  /**
   * Priced from EZX's OWN rate tables (an active supply channel with no API
   * provider) rather than bought through an integration like ShipStation.
   *
   * ⚠️ NOT "EZX's own fleet" — EZX owns nothing physical, and any partner
   * carrier that prices itself in the carrier portal is self-rated too.
   * Answered by the server; never re-derive it from raw channels here.
   */
  selfRated: boolean;
  activeChannelCount?: number;
  /**
   * Customer marketplace visibility, independent of the source of rates.
   */
  visibility?: CarrierVisibility;
  /** Switched on for every customer the moment they sign up. */
  defaultEnabled?: boolean;
  /** false = the customer's toggle is dead; the platform owns the state. */
  customerCanToggle?: boolean;

  // Multi-provider support
  providers?: {
    id: string;
    carrierId: string;
    apiProviderId: string;
    active: boolean;
    services?: string[];
    apiProvider: {
      id: string;
      code: string;
      name: string;
    };
  }[];

  // Capacity. No isLocalFleet/fleetOperator: EZX owns nothing physical, so
  // there is no house fleet to flag. The server sends neither.
  maxDailyCapacity?: number;
  vehicleCount?: number;

  // Operations
  operatingHours?: Record<string, unknown>;
  contactEmail?: string;
  contactPhone?: string;

  // Branding
  logoUrl?: string | null;

  // Services — Phase 15b: sourced from CarrierService rows by the server,
  // shaped as { code, displayName }. Older nested fields (name, description,
  // metadata) are kept on the type only to avoid breaking other readers
  // during the rollout window.
  services?: {
    code: string;
    name?: string;
    displayName?: string;
    description?: string;
    metadata?: Record<string, unknown>;
  }[];

  // Phase 15b aggregate from the server — used by the carriers-table
  // empty-state CTA. Undefined on legacy responses.
  serviceCount?: number;
  // No activeProviderCount: no server aggregate exists; the carriers table
  // counts active entries in `providers` instead.

  // Base costs
  // No pickupHandlingFeeCents: EZX's cut lives on PlatformFee.

  // No `priority`: ordering lives on SupplyChannel (one carrier can be reached
  // several ways, each with its own precedence), not on the carrier itself.

  // Status
  active: boolean;
  createdAt: string;
  updatedAt: string;

  // Auto-discovery
  autoDiscovered?: boolean;
  metadata?: Record<string, unknown>;

  // Health and credential status (populated by API)
  health?: CarrierHealthStatus;
  credentials?: CarrierCredentialStatus;
}

export interface CarrierAlias {
  id: string;
  carrierId: string;
  alias: string;
  canonical: string;
  createdAt: string;
  updatedAt: string;
}

export interface CarrierResolutionRule {
  id: string;
  carrierCode: string;
  serviceCode?: string;
  mode: ResolutionMode;
  preferredCarrierId?: string;
  note?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CarrierServiceArea {
  id: string;
  carrierId: string;
  serviceAreaId: string;
  maxDailyOrders?: number;
}

// ========================= API Hooks =========================

// Get carrier aliases
export function useGetCarrierAliases(params?: {
  carrierId?: string;
  page?: number;
  limit?: number;
}) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["carrier-aliases", params],
    queryFn: async () => {
      const { data } = await axios.get<{
        data: CarrierAlias[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          pages: number;
        };
      }>(`${API_URL}/api/v2/carrier-aliases`, {
        params: {
          carrierId: params?.carrierId,
          page: params?.page ?? 1,
          limit: params?.limit ?? 50,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return data;
    },
    enabled: !!token,
  });
}

// Create carrier alias
export function useCreateCarrierAlias(options?: {
  onSuccess?: (data: CarrierAlias) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (alias: {
      carrierId: string;
      alias: string;
      canonical: string;
    }) => {
      const { data } = await axios.post<{
        data: CarrierAlias;
      }>(`${API_URL}/api/v2/carrier-aliases`, alias, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return data.data;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["carrier-aliases"] });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

// Get resolution rules
export function useGetResolutionRules(params?: {
  carrierCode?: string;
  page?: number;
  limit?: number;
}) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["resolution-rules", params],
    queryFn: async () => {
      const { data } = await axios.get<{
        data: CarrierResolutionRule[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          pages: number;
        };
      }>(`${API_URL}/api/v2/carrier-resolution-rules`, {
        params: {
          carrierCode: params?.carrierCode,
          page: params?.page ?? 1,
          limit: params?.limit ?? 50,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return data;
    },
    enabled: !!token,
  });
}

// Create/Update resolution rule
export function useUpsertResolutionRule(options?: {
  onSuccess?: (data: CarrierResolutionRule) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (rule: {
      carrierCode: string;
      serviceCode?: string;
      mode: ResolutionMode;
      preferredCarrierId?: string;
      note?: string;
    }) => {
      const { data } = await axios.post<{
        data: CarrierResolutionRule;
      }>(`${API_URL}/api/v2/carrier-resolution-rules`, rule, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return data.data;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["resolution-rules"] });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

// Get carrier service areas
export function useGetCarrierServiceAreas(carrierId: string) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["carrier-service-areas", carrierId],
    queryFn: async () => {
      const { data } = await axios.get<{
        data: CarrierServiceArea[];
      }>(`${API_URL}/api/v2/shipping/carriers/${carrierId}/service-areas`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return data.data;
    },
    enabled: !!token && !!carrierId,
  });
}

// ========================= Create Carrier =========================

export function useCreateCarrier(options?: {
  onSuccess?: (data: Carrier) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      carrier: Partial<Carrier> & {
        code: string;
        name: string;
        // Carrier-level markup, created atomically with the carrier server-side.
        // Server defaults to 0% FIXED + $0 processing if omitted, but the UI
        // surfaces these fields so admins make the call intentionally.
        platformFee?: {
          percent: number;
          flatCents: number;
        };
      }
    ) => {
      const { data } = await axios.post<{ data: Carrier }>(
        `${API_URL}/api/v2/carriers`,
        carrier,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data.data;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["carriers"] });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

// ========================= Additional Carrier Hooks =========================

// Get all carriers
// No `type` filter: the server has no carrier type to filter on.
export function useGetCarriers(params?: { active?: boolean }) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["carriers", params],
    queryFn: async ({ signal }) => {
      const carriers: Carrier[] = [];
      let page = 1;
      let pages = 1;
      do {
        const { data } = await axios.get<{
          data: Carrier[];
          pagination: { pages: number };
        }>(`${API_URL}/api/v2/carriers`, {
          params: { ...params, page },
          headers: { Authorization: `Bearer ${token}` },
          signal,
        });
        carriers.push(...data.data);
        pages = data.pagination.pages;
        page += 1;
      } while (page <= pages);
      return { data: carriers };
    },
    enabled: !!token,
  });
}

// Alias for backward compatibility
export const useCarriers = useGetCarriers;

// Update carrier settings
export function useUpdateCarrier(options?: {
  onSuccess?: (data: Carrier) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<Carrier>;
    }) => {
      const response = await axios.patch<{ data: Carrier }>(
        `${API_URL}/api/v2/carriers/${id}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data.data;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["carriers"] });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}
